import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {
  PlaygroundSalesOrder,
  PlaygroundLine,
  PlaygroundPayment,
  PlaygroundAllocation,
  PlaygroundProductType,
  PlaygroundSyncQueue,
  PlaygroundLookups,
  PlaygroundTravelerForm
} from '../model/playground.model';

@Injectable({providedIn: 'root'})
export class PlaygroundService {
  private readonly basePath = 'tmt/playground';

  constructor(private http: HttpClient) {}

  getSalesOrders(): Observable<PlaygroundSalesOrder[]> {
    return this.http.get<PlaygroundSalesOrder[]>(this.basePath);
  }

  getSalesOrder(id: number): Observable<PlaygroundSalesOrder> {
    return this.http.get<PlaygroundSalesOrder>(`${this.basePath}/${id}`);
  }

  getProductTypes(): Observable<PlaygroundProductType[]> {
    return this.http.get<PlaygroundProductType[]>(`${this.basePath}/product-types`);
  }

  getLookups(): Observable<PlaygroundLookups> {
    return this.http.get<PlaygroundLookups>(`${this.basePath}/lookups`);
  }

  createSalesOrder(order: Partial<PlaygroundSalesOrder>): Observable<PlaygroundSalesOrder> {
    return this.http.post<PlaygroundSalesOrder>(`${this.basePath}/sales-orders`, order);
  }

  updateSalesOrder(order: Record<string, unknown>): Observable<void> {
    return this.http.put<void>(`${this.basePath}/sales-orders`, order);
  }

  deleteSalesOrder(id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/sales-orders/${id}`);
  }

  createLine(line: Partial<PlaygroundLine>): Observable<PlaygroundLine> {
    return this.http.post<PlaygroundLine>(`${this.basePath}/lines`, line);
  }

  updateLine(line: Partial<PlaygroundLine>): Observable<void> {
    return this.http.put<void>(`${this.basePath}/lines`, line);
  }

  deleteLine(id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/lines/${id}`);
  }

  createPayment(payment: Partial<PlaygroundPayment>): Observable<PlaygroundPayment> {
    return this.http.post<PlaygroundPayment>(`${this.basePath}/payments`, payment);
  }

  deletePayment(id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/payments/${id}`);
  }

  createAllocation(allocation: Partial<PlaygroundAllocation>): Observable<PlaygroundAllocation> {
    return this.http.post<PlaygroundAllocation>(`${this.basePath}/allocations`, allocation);
  }

  deleteAllocation(id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/allocations/${id}`);
  }

  triggerSync(salesOrderId: number): Observable<PlaygroundSyncQueue> {
    return this.http.post<PlaygroundSyncQueue>(`${this.basePath}/trigger-sync/${salesOrderId}`, {});
  }

  sendToErpQueue(salesOrderId: number): Observable<unknown> {
    return this.http.post('erp/travel-operations-tmt/sales-order/send-to-queue', {salesOrderId});
  }

  startTrip(salesOrderId: number): Observable<void> {
    return this.http.post<void>(`${this.basePath}/start-trip/${salesOrderId}`, {});
  }

  cancelTrip(salesOrderId: number): Observable<void> {
    return this.http.post<void>(`${this.basePath}/cancel-trip/${salesOrderId}`, {});
  }

  createTraveler(traveler: PlaygroundTravelerForm): Observable<number> {
    return this.http.post<number>(`${this.basePath}/travelers`, traveler);
  }

  deleteTraveler(id: number): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/travelers/${id}`);
  }

  markFamTrip(salesOrderId: number, isFamTrip: boolean): Observable<void> {
    return this.http.post<void>(
      `${this.basePath}/mark-fam/${salesOrderId}?isFamTrip=${isFamTrip}`, {});
  }
}
